const identifier = /^[A-Za-z_][A-Za-z0-9_]*$/;
const divider = '-'.repeat(80);
function destinationLines(destination) {
  if (!destination || !Object.hasOwn(destination, 'title') || !Object.hasOwn(destination, 'subTitle')) {
    throw new Error('Actual destination title/sub-title metadata is required to compose this template.');
  }
  return ['title', 'subTitle'].map(key => {
    const value = destination[key] ?? '';
    if (typeof value !== 'string' || /[{}]/.test(value)) {
      throw new Error('Destination metadata contains braces or unsupported text and cannot be safely inserted. Enter the header manually for a new connection; existing conversions require safe saved metadata.');
    }
    return value.replace(/\r/g, ' ').replace(/\n/g, ' ');
  });
}

export function newChunkTemplate(destination) {
  const [title, subTitle] = destination ? destinationLines(destination) : ['', ''];
  return `${divider}\ntitle: ${title}\nsub-title: ${subTitle}\ntopic: {{ th_content_title }}\nsub-topic:\nsource: News & Activities content_id={{ content_id }}\n${divider}\n\n{{ th_detail }}`;
}

export function editableChunkTemplate(settings, destination) {
  if (settings.chunkTemplateIncludesHeader === true) {
    if (settings.chunkTemplate == null) throw new Error('Complete chunk format requires an explicit nonblank template.');
    validateTemplate(settings.chunkTemplate);
    return settings.chunkTemplate;
  }
  const [title, subTitle] = destinationLines(destination);
  let template;
  if (settings.chunkTemplate != null) {
    validateTemplate(settings.chunkTemplate);
    template = `${divider}\ntitle: ${title}\nsub-title: ${subTitle}\nsource: News & Activities content_id={{ content_id }}\n${divider}\n\n${settings.chunkTemplate}`;
  } else {
    const topic = resolveTemplate(settings.topicTemplate, settings.topicField ?? 'th_content_title');
    const content = resolveTemplate(settings.contentTemplate, settings.contentField ?? 'th_detail');
    const published = resolveTemplate(null, settings.publishedField ?? 'publish_at');
    template = `${divider}\ntitle: ${title}\nsub-title: ${subTitle}\ntopic: ${topic}\nsub-topic: \nsource: News & Activities content_id={{ content_id }}\npublished: ${published}\n${divider}\n\n${content}`;
  }
  validateTemplate(template);
  return template;
}
export function formatChunkPreview(title, subTitle, contentId, rendered) {
  if (!Number.isSafeInteger(contentId)) throw new Error('Complete header requires an exact safe-integer sample content_id.');
  const line = value => (value ?? '').replace(/\r/g, ' ').replace(/\n/g, ' ');
  const rule = '-'.repeat(80);
  return `${rule}\ntitle: ${line(title)}\nsub-title: ${line(subTitle)}\nsource: News & Activities content_id=${String(contentId)}\n${rule}\n\n${rendered}`;
}
const reserved = new Set(['files', '__proto__', 'prototype', 'constructor', ...Object.getOwnPropertyNames(Object.prototype)]);
const safeField = field => typeof field === 'string' && identifier.test(field) && !reserved.has(field);
const scalar = value => value === null || typeof value === 'string' || typeof value === 'boolean'
  || (typeof value === 'number' && Number.isSafeInteger(value));

export function resolveTemplate(template, legacyField) {
  if (template == null) {
    if (!safeField(legacyField)) throw new Error('Invalid or reserved legacy field.');
    template = `{{ ${legacyField} }}`;
  }
  validateTemplate(template);
  return template;
}

export function resolveChunkTemplate(settings = {}) {
  const template = settings.chunkTemplate ?? [
    `topic: ${resolveTemplate(settings.topicTemplate, settings.topicField ?? 'th_content_title')}`,
    'sub-topic:',
    `published: ${resolveTemplate(null, settings.publishedField ?? 'publish_at')}`,
    '',
    resolveTemplate(settings.contentTemplate, settings.contentField ?? 'th_detail'),
  ].join('\n');
  validateTemplate(template);
  return template;
}

export function validateTemplate(template) {
  if (typeof template !== 'string' || !template.trim()) throw new Error('Template must not be blank.');
  if (template.length > 100000) throw new Error('Template exceeds 100000 UTF-16 code units.');
  const parts = [];
  let cursor = 0;
  // Scan once, including malformed input, instead of repeatedly searching unmatched openers.
  for (let index = 0; index < template.length;) {
    if (template[index] !== '{' && template[index] !== '}') { index++; continue; }
    if (template[index] !== '{' || template[index + 1] !== '{') throw new Error('Malformed template delimiter.');
    let close = index + 2;
    while (close < template.length && template[close] !== '}') {
      if (template[close] === '{') throw new Error('Malformed template delimiter.');
      close++;
    }
    if (template[close + 1] !== '}') throw new Error('Malformed template delimiter.');
    const literal = template.slice(cursor, index);
    const token = template.slice(index + 2, close).trim();
    const field = token.startsWith('$.') ? token.slice(2) : token;
    if (!identifier.test(field)) throw new Error('Malformed template field token.');
    if (reserved.has(field)) throw new Error(`Reserved field: ${field}.`);
    parts.push({ text: literal }, { field });
    index = close + 2;
    cursor = index;
  }
  const tail = template.slice(cursor);
  if (/[{}]/.test(tail)) throw new Error('Malformed template delimiter.');
  parts.push({ text: tail });
  return parts;
}

export function renderTemplate(template, article) {
  const parts = validateTemplate(template);
  if (article === null || typeof article !== 'object' || Array.isArray(article)) {
    throw new Error('Template article must be a JSON object.');
  }
  let length = 0;
  const output = parts.map(part => {
    let text = part.text;
    if (part.field !== undefined) {
      const field = part.field;
      if (!article || !Object.hasOwn(article, field)) throw new Error(`Unknown field: ${field}.`);
      const value = article[field];
      if (typeof value === 'number' && !Number.isSafeInteger(value)) {
        throw new Error(`Unsupported value in field ${field}: numbers must be safe integers.`);
      }
      if (!scalar(value)) throw new Error(`Unsupported value in field ${field}: expected a scalar value.`);
      text = value === null ? '' : String(value);
    }
    length += text.length;
    if (length > 1000000) throw new Error('Rendered template exceeds 1000000 UTF-16 code units.');
    return text;
  }).join('');
  return output;
}

export function scalarFields(article) {
  return article && typeof article === 'object'
    ? Object.keys(article).filter(field => safeField(field) && scalar(article[field])) : [];
}
